import { createFileRoute } from "@tanstack/react-router";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";

export const Route = createFileRoute("/refund")({
  head: () => ({
    meta: [
      { title: "Refund Policy — legaldecoder" },
      {
        name: "description",
        content: "Our 30-day money-back guarantee and how to request a refund through Paddle.",
      },
      { property: "og:title", content: "Refund Policy — legaldecoder" },
      { property: "og:description", content: "30-day money-back guarantee. No questions asked." },
      { property: "og:url", content: "https://legaldecoder.io/refund" },
    ],
    links: [{ rel: "canonical", href: "https://legaldecoder.io/refund" }],
  }),
  component: Refund,
});

function Refund() {
  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="mx-auto max-w-3xl px-4 py-16 sm:px-6 lg:px-8">
        <h1 className="text-4xl font-bold tracking-tight">Refund Policy</h1>
        <p className="mt-2 text-sm text-muted-foreground">Last updated: April 2026</p>

        <div className="mt-10 rounded-2xl border-2 border-success/30 bg-success/5 p-6">
          <h2 className="text-xl font-bold text-success">💚 30-Day Money-Back Guarantee</h2>
          <p className="mt-3 text-foreground">
            If legaldecoder isn't right for you, request a full refund within{" "}
            <strong>30 days</strong> of your order — no questions asked.
          </p>
        </div>

        <div className="prose prose-slate mt-8 max-w-none space-y-6 text-foreground">
          <section>
            <h2 className="text-xl font-semibold">Who handles refunds</h2>
            <p className="mt-2 text-muted-foreground">
              Our online reseller, <strong>Paddle.com</strong>, is the Merchant of Record for all
              legaldecoder orders. Paddle processes payments, invoices, and refunds on our behalf.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold">How to request a refund</h2>
            <ol className="mt-2 list-decimal space-y-2 pl-6 text-muted-foreground">
              <li>
                Go to the Paddle customer portal at{" "}
                <a className="underline" href="https://paddle.net" target="_blank" rel="noreferrer">
                  paddle.net
                </a>{" "}
                and sign in with the email address you used at checkout.
              </li>
              <li>
                Find the relevant order and select "Request a refund", or reply to your Paddle
                receipt email.
              </li>
              <li>
                Alternatively, email us at{" "}
                <a className="underline" href="mailto:billing@legaldecoder.io">
                  billing@legaldecoder.io
                </a>{" "}
                and we'll forward your request to Paddle.
              </li>
            </ol>
            <p className="mt-3 text-muted-foreground">
              Approved refunds are issued to the original payment method, typically within 3–10
              business days depending on your bank or card issuer.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold">Subscription cancellations</h2>
            <p className="mt-2 text-muted-foreground">
              You can cancel a subscription at any time from your{" "}
              <a className="underline" href="/account">
                account page
              </a>{" "}
              or through the Paddle customer portal. Cancellation stops future renewals; you keep
              Pro access until the end of the current paid period. Cancellation alone does not
              trigger a refund — to request money back for the most recent charge, follow the steps
              above within the 30-day window.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold">Statutory rights</h2>
            <p className="mt-2 text-muted-foreground">
              This policy is offered in addition to any consumer-protection rights available to you
              under the law of your country (such as the EU/UK 14-day right of withdrawal for
              digital services). Nothing in this policy limits those statutory rights.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold">Questions</h2>
            <p className="mt-2 text-muted-foreground">
              For billing or refund questions:{" "}
              <a className="underline" href="mailto:billing@legaldecoder.io">
                billing@legaldecoder.io
              </a>
              . For everything else, see our{" "}
              <a className="underline" href="/terms">
                Terms of Service
              </a>{" "}
              and{" "}
              <a className="underline" href="/privacy">
                Privacy Policy
              </a>
              .
            </p>
          </section>
        </div>
      </main>
      <Footer />
    </div>
  );
}
